import { CollapsibleFilters } from '../common/CollapsibleFilters';
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Stack,
  Alert,
  LinearProgress,
  Grid,
  Divider,
} from '@mui/joy';
import {
  Plus as AddIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  MagnifyingGlass as SearchIcon,
  Buildings as BusinessIcon,
  Star as StarIcon,
  LinkSimple as LinkIcon,
} from '@phosphor-icons/react';
import { useCompetitors, useCreateCompetitor, useUpdateCompetitor, useDeleteCompetitor } from '../../hooks/useCompetitors';
import type { CompetitorTracking } from '../../types/marketing';

interface CompetitorFormData {
  competitor_name: string;
  competitor_website: string;
  gbp_url: string;
  service_areas: string;
  tracked_keywords: string;
  notes: string;
  gbp_reviews?: number | null;
  gbp_rating?: number | null;
  domain_authority?: number | null;
  organic_keywords?: number | null;
  backlinks?: number | null;
  citation_count?: number | null;
}

export const CompetitorsManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState<CompetitorTracking | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<CompetitorFormData>({
    competitor_name: '',
    competitor_website: '',
    gbp_url: '',
    service_areas: '',
    tracked_keywords: '',
    notes: '',
    gbp_reviews: null,
    gbp_rating: null,
    domain_authority: null,
    organic_keywords: null,
    backlinks: null,
    citation_count: null,
  });

  const { data: competitors = [], isLoading, error } = useCompetitors();
  const createMutation = useCreateCompetitor();
  const updateMutation = useUpdateCompetitor();
  const deleteMutation = useDeleteCompetitor();

  const handleOpenModal = (competitor?: CompetitorTracking) => {
    if (competitor) {
      setEditingCompetitor(competitor);
      setFormData({
        competitor_name: competitor.competitor_name,
        competitor_website: competitor.competitor_website,
        gbp_url: competitor.gbp_url,
        service_areas: competitor.service_areas,
        tracked_keywords: competitor.tracked_keywords,
        notes: competitor.notes,
        gbp_reviews: competitor.gbp_reviews,
        gbp_rating: competitor.gbp_rating,
        domain_authority: competitor.domain_authority,
        organic_keywords: competitor.organic_keywords,
        backlinks: competitor.backlinks,
        citation_count: competitor.citation_count,
      });
    } else {
      setEditingCompetitor(null);
      setFormData({
        competitor_name: '',
        competitor_website: '',
        gbp_url: '',
        service_areas: '',
        tracked_keywords: '',
        notes: '',
        gbp_reviews: null,
        gbp_rating: null,
        domain_authority: null,
        organic_keywords: null,
        backlinks: null,
        citation_count: null,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCompetitor(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingCompetitor) {
        await updateMutation.mutateAsync({
          id: editingCompetitor.id,
          ...formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save competitor:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this competitor?')) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        console.error('Failed to delete competitor:', error);
      }
    }
  };

  const getDomainAuthorityColor = (da: number | null) => {
    if (!da) return 'neutral';
    if (da >= 60) return 'success';
    if (da >= 40) return 'warning';
    return 'danger';
  };

  const filteredCompetitors = competitors.filter((competitor: CompetitorTracking) =>
    competitor.competitor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    competitor.competitor_website.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (error) {
    return (
      <Alert color="danger">
        Failed to load competitors. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography level="h3">Competitor Tracking</Typography>
        <Button
          startDecorator={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          Add Competitor
        </Button>
      </Box>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <CollapsibleFilters><Input
              placeholder="Search competitors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startDecorator={<SearchIcon />}
              sx={{ flex: 1 }}
            /></CollapsibleFilters>
          </Box>

          {isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading competitors...</Typography>
            </Box>
          ) : filteredCompetitors.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="neutral" sx={{ mb: 2 }}>
                {searchTerm ? 'No competitors found matching your search.' : 'No competitors tracked yet.'}
              </Typography>
              {!searchTerm && (
                <Button
                  variant="soft"
                  startDecorator={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Track Your First Competitor
                </Button>
              )}
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredCompetitors.map((competitor: CompetitorTracking) => (
                <Grid key={competitor.id} xs={12} md={6} lg={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography level="title-lg" sx={{ mb: 0.5 }}>
                            {competitor.competitor_name}
                          </Typography>
                          <Typography
                            level="body-sm"
                            component="a"
                            href={competitor.competitor_website}
                            target="_blank"
                            rel="noopener noreferrer"
                            startDecorator={<LinkIcon size={14} />}
                            sx={{
                              textDecoration: 'none',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {competitor.competitor_website.replace(/^https?:\/\//, '')}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => handleOpenModal(competitor)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => handleDelete(competitor.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </Box>

                      {competitor.gbp_url && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            level="body-sm"
                            component="a"
                            href={competitor.gbp_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startDecorator={<BusinessIcon size={14} />}
                            sx={{
                              textDecoration: 'none',
                              color: 'neutral.600',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            Google Business Profile
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        {competitor.gbp_rating && (
                          <Chip
                            variant="soft"
                            color="warning"
                            size="sm"
                            startDecorator={<StarIcon />}
                          >
                            {competitor.gbp_rating} ({competitor.gbp_reviews || 0} reviews)
                          </Chip>
                        )}
                        {competitor.domain_authority && (
                          <Chip
                            variant="soft"
                            color={getDomainAuthorityColor(competitor.domain_authority)}
                            size="sm"
                          >
                            DA: {competitor.domain_authority}
                          </Chip>
                        )}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={2}>
                        <Grid xs={6}>
                          <Typography level="body-xs" color="neutral">Organic Keywords</Typography>
                          <Typography level="body-sm" fontWeight="md">
                            {competitor.organic_keywords?.toLocaleString() || '-'}
                          </Typography>
                        </Grid>
                        <Grid xs={6}>
                          <Typography level="body-xs" color="neutral">Backlinks</Typography>
                          <Typography level="body-sm" fontWeight="md">
                            {competitor.backlinks?.toLocaleString() || '-'}
                          </Typography>
                        </Grid>
                        <Grid xs={6}>
                          <Typography level="body-xs" color="neutral">Citations</Typography>
                          <Typography level="body-sm" fontWeight="md">
                            {competitor.citation_count?.toLocaleString() || '-'}
                          </Typography>
                        </Grid>
                        <Grid xs={6}>
                          <Typography level="body-xs" color="neutral">Last Analyzed</Typography>
                          <Typography level="body-sm" fontWeight="md">
                            {competitor.last_analyzed
                              ? new Date(competitor.last_analyzed).toLocaleDateString()
                              : 'Never'}
                          </Typography>
                        </Grid>
                      </Grid>

                      {competitor.service_areas && (
                        <Box sx={{ mt: 2 }}>
                          <Typography level="body-xs" color="neutral">Service Areas</Typography>
                          <Typography level="body-sm">
                            {competitor.service_areas}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <ModalDialog sx={{ width: 600, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4">
            {editingCompetitor ? 'Edit Competitor' : 'Add New Competitor'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <FormControl required>
                <FormLabel>Competitor Name</FormLabel>
                <Input
                  value={formData.competitor_name}
                  onChange={(e) => setFormData({ ...formData, competitor_name: e.target.value })}
                  placeholder="e.g., ABC Roofing"
                />
              </FormControl>

              <FormControl required>
                <FormLabel>Website</FormLabel>
                <Input
                  value={formData.competitor_website}
                  onChange={(e) => setFormData({ ...formData, competitor_website: e.target.value })}
                  placeholder="https://example.com"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Google Business Profile URL</FormLabel>
                <Input
                  value={formData.gbp_url}
                  onChange={(e) => setFormData({ ...formData, gbp_url: e.target.value })}
                  placeholder="https://maps.google.com/..."
                />
              </FormControl>

              <Typography level="title-sm" sx={{ mt: 2 }}>GBP Metrics</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <FormControl>
                  <FormLabel>Reviews</FormLabel>
                  <Input
                    type="number"
                    value={formData.gbp_reviews || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      gbp_reviews: e.target.value ? parseInt(e.target.value) : null
                    })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Rating</FormLabel>
                  <Input
                    type="number"
                    slotProps={{
                      input: {
                        step: "0.1"
                      }
                    }}
                    value={formData.gbp_rating || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      gbp_rating: e.target.value ? parseFloat(e.target.value) : null
                    })}
                    placeholder="0-5"
                  />
                </FormControl>
              </Box>

              <Typography level="title-sm" sx={{ mt: 2 }}>SEO Metrics</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <FormControl>
                  <FormLabel>Domain Authority</FormLabel>
                  <Input
                    type="number"
                    value={formData.domain_authority || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      domain_authority: e.target.value ? parseInt(e.target.value) : null
                    })}
                    placeholder="0-100"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Organic Keywords</FormLabel>
                  <Input
                    type="number"
                    value={formData.organic_keywords || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      organic_keywords: e.target.value ? parseInt(e.target.value) : null
                    })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Backlinks</FormLabel>
                  <Input
                    type="number"
                    value={formData.backlinks || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      backlinks: e.target.value ? parseInt(e.target.value) : null
                    })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Citations</FormLabel>
                  <Input
                    type="number"
                    value={formData.citation_count || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      citation_count: e.target.value ? parseInt(e.target.value) : null
                    })}
                  />
                </FormControl>
              </Box>

              <FormControl>
                <FormLabel>Service Areas</FormLabel>
                <Textarea
                  value={formData.service_areas}
                  onChange={(e) => setFormData({ ...formData, service_areas: e.target.value })}
                  placeholder="Denver, Aurora, Littleton..."
                  minRows={2}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Tracked Keywords</FormLabel>
                <Textarea
                  value={formData.tracked_keywords}
                  onChange={(e) => setFormData({ ...formData, tracked_keywords: e.target.value })}
                  placeholder="Keywords they rank for..."
                  minRows={2}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  minRows={3}
                />
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button variant="plain" color="neutral" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
                  {editingCompetitor ? 'Update' : 'Add'} Competitor
                </Button>
              </Box>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
